import React, { useMemo, useState, useCallback, useSyncExternalStore } from 'react';
import { useSelector } from 'react-redux';
import { Card, Button, Spinner } from 'react-bootstrap';
import { User, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';
import { useFetchHealthRadarQuery, normalizePreventionShieldApiLanguage } from '#store';
import { HealthProfileBreakdownModal } from './HealthProfileBreakdownModal';
import { formatHealthRadarDisclaimer } from './formatHealthRadarDisclaimer';

/** Ciemnozielony kolor marki (Volt / teal-700) — radar: stroke + fill z przezroczystością */
const BRAND_GREEN = '#0D5C53';

function clampScore(n) {
  if (!Number.isFinite(n)) return null;
  return Math.min(100, Math.max(0, n));
}

const NARROW_CHART_MQ = '(max-width: 575.98px)';

function subscribeNarrowChart(callback) {
  const mq = window.matchMedia(NARROW_CHART_MQ);
  mq.addEventListener('change', callback);
  return () => mq.removeEventListener('change', callback);
}

function getNarrowChartSnapshot() {
  return window.matchMedia(NARROW_CHART_MQ).matches;
}

function getServerSnapshot() {
  return false;
}

/** Mniejszy promień + większe marginesy na telefonach — etykiety osi nie są obcinane. */
function useNarrowHealthProfileChart() {
  return useSyncExternalStore(subscribeNarrowChart, getNarrowChartSnapshot, getServerSnapshot);
}

const RADAR_ANGLE_TICK_LINE = '1.1em';

/** Na wąsku: łamanie długich etykiet (np. „Zdrowie psych.” → dwie linie). */
function splitNarrowRadarAxisLabel(text) {
  if (typeof text !== 'string' || text.length < 10) return null;
  const i = text.indexOf(' ');
  if (i <= 0 || i >= text.length - 1) return null;
  const rest = text.slice(i + 1).trim();
  if (!rest) return null;
  return [text.slice(0, i), rest];
}

/** Odstępy między liniami zgodne z logiką verticalAnchor w Recharts `<Text />`. */
function radarAngleTickTspanDy(verticalAnchor) {
  switch (verticalAnchor) {
    case 'start':
      return { first: '0.35em', second: RADAR_ANGLE_TICK_LINE };
    case 'middle':
      return { first: '-0.55em', second: RADAR_ANGLE_TICK_LINE };
    case 'end':
    default:
      return { first: `-${RADAR_ANGLE_TICK_LINE}`, second: RADAR_ANGLE_TICK_LINE };
  }
}

/**
 * Profil zdrowotny: GET /medapp/health-radar/ (etykiety osi + disclaimer z API).
 */
export function HealthProfileCard() {
  const { t, i18n } = useTranslation();
  const userToken = useSelector((s) => s.auth.userToken);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const narrowChart = useNarrowHealthProfileChart();

  const openBreakdown = useCallback(() => setBreakdownOpen(true), []);
  const closeBreakdown = useCallback(() => setBreakdownOpen(false), []);

  const renderPolarAngleTick = useCallback(
    (props) => {
      const { x, y, textAnchor, verticalAnchor, fill, payload } = props;
      const fontSize = narrowChart ? 10 : 12;
      const fillColor = fill ?? 'var(--muted-foreground)';
      const value = payload?.value ?? '';
      const parts = narrowChart ? splitNarrowRadarAxisLabel(value) : null;
      const className = 'recharts-text recharts-polar-angle-axis-tick-value';

      if (!parts) {
        return (
          <text
            x={x}
            y={y}
            textAnchor={textAnchor}
            fill={fillColor}
            fontSize={fontSize}
            className={className}
          >
            {value}
          </text>
        );
      }

      const { first, second } = radarAngleTickTspanDy(verticalAnchor);

      return (
        <text
          x={x}
          y={y}
          textAnchor={textAnchor}
          fill={fillColor}
          fontSize={fontSize}
          className={className}
        >
          <tspan x={x} dy={first}>
            {parts[0]}
          </tspan>
          <tspan x={x} dy={second}>
            {parts[1]}
          </tspan>
        </text>
      );
    },
    [narrowChart],
  );

  const onKeyActivate = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openBreakdown();
      }
    },
    [openBreakdown],
  );

  const apiLanguage = useMemo(
    () => normalizePreventionShieldApiLanguage(i18n.language),
    [i18n.language],
  );

  const { data, isLoading, isError, isFetching, refetch } = useFetchHealthRadarQuery(apiLanguage, {
    skip: !userToken,
  });

  const chartData = useMemo(() => {
    if (!data?.axes?.length) return [];
    return data.axes.map((ax) => {
      const raw = ax.score;
      const n = Number(raw);
      const fullMark = Number(ax.fullMark);
      const hasMark = Number.isFinite(fullMark) && fullMark > 0;
      let value = null;
      if (ax.hasData && raw != null && Number.isFinite(n)) {
        value = hasMark ? clampScore((n / fullMark) * 100) : clampScore(n);
      }
      return {
        subject: ax.subject ?? ax.axis ?? '',
        value,
      };
    });
  }, [data]);

  const hasAnyScore = useMemo(
    () => chartData.some((d) => d.value != null && Number.isFinite(d.value)),
    [chartData],
  );

  const showChartLoading = isLoading && !data;
  const disclaimer = useMemo(
    () =>
      formatHealthRadarDisclaimer(
        typeof data?.disclaimer === 'string' ? data.disclaimer : '',
        data?.language ?? apiLanguage,
      ),
    [data?.disclaimer, data?.language, apiLanguage],
  );

  return (
    <>
      <Card className='health-profile-bootstrap-card d-flex h-100 w-100 flex-column border-0 shadow p-0'>
        <div
          role='button'
          tabIndex={0}
          className='health-profile-card__clickable d-flex flex-column flex-grow-1 min-h-0 h-100 w-100 cursor-pointer transition-colors duration-200 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0D5C53]'
          onClick={openBreakdown}
          onKeyDown={onKeyActivate}
          aria-label={t('dashboard.health-profile-open-details-aria')}
          aria-haspopup='dialog'
          aria-expanded={breakdownOpen}
        >
          <Card.Header className='bg-transparent border-bottom py-3 pb-2'>
            <div
              className='mb-0 d-flex align-items-center gap-2 fw-semibold text-dark w-100'
              style={{ fontSize: '1rem' }}
            >
              <User
                className='flex-shrink-0'
                style={{ width: '1.25rem', height: '1.25rem', color: BRAND_GREEN }}
                strokeWidth={2}
                aria-hidden
              />
              <span className='flex-grow-1 text-start'>
                {t('dashboard.health-profile-card-title')}
              </span>
              <span className='d-flex align-items-center gap-2 flex-shrink-0 ms-auto'>
                {isFetching && !isLoading ? (
                  <Spinner animation='border' size='sm' className='opacity-50' aria-hidden />
                ) : null}
                <Info className='text-muted flex-shrink-0' size={18} strokeWidth={2} aria-hidden />
              </span>
            </div>
          </Card.Header>
          <Card.Body className='health-profile-card__body d-flex flex-column flex-grow-1 min-h-0 pt-3 pb-4 bg-transparent'>
            {isError ? (
              <div
                className='mt-2 text-center'
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                role='presentation'
              >
                <p className='mb-2 text-xs text-muted-foreground'>
                  {t('dashboard.health-profile-fetch-error')}
                </p>
                <Button
                  variant='outline-secondary'
                  size='sm'
                  type='button'
                  onClick={() => refetch()}
                >
                  {t('dashboard.health-profile-retry')}
                </Button>
              </div>
            ) : null}

            {!isError && showChartLoading ? (
              <div
                className='d-flex flex-grow-1 align-items-center justify-content-center'
                style={{ minHeight: 220 }}
                role='status'
                aria-live='polite'
                aria-label={t('dashboard.health-profile-loading-aria')}
              >
                <Spinner animation='border' size='sm' className='text-muted' />
              </div>
            ) : null}

            {!isError && !showChartLoading && chartData.length > 0 ? (
              <div className='health-profile-card__chart-wrap w-100 d-flex flex-column'>
                {!hasAnyScore ? (
                  <p className='mb-2 px-2 text-center text-sm text-muted-foreground' role='status'>
                    {t('dashboard.health-profile-empty')}
                  </p>
                ) : null}
                <ResponsiveContainer width='100%' height='100%' className='flex-grow-1'>
                  <RadarChart
                    data={chartData}
                    cx='50%'
                    cy='50%'
                    outerRadius={narrowChart ? '62%' : '82%'}
                    margin={
                      narrowChart
                        ? { top: 10, right: 14, bottom: 38, left: 14 }
                        : { top: 10, right: 16, bottom: 10, left: 16 }
                    }
                  >
                    <PolarGrid stroke='var(--border)' />
                    <PolarAngleAxis dataKey='subject' tick={renderPolarAngleTick} />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} />
                    {hasAnyScore ? (
                      <Radar
                        name={t('dashboard.health-profile-radar-series')}
                        dataKey='value'
                        stroke={BRAND_GREEN}
                        fill={BRAND_GREEN}
                        fillOpacity={0.5}
                        strokeWidth={2}
                        connectNulls={false}
                      />
                    ) : null}
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            ) : null}

            {!isError && !showChartLoading && userToken && chartData.length === 0 ? (
              <p className='mb-0 text-center text-sm text-muted-foreground' role='status'>
                {t('dashboard.health-profile-empty')}
              </p>
            ) : null}

            {!isError && disclaimer ? (
              <p className='mb-0 mt-3 px-1 text-center text-xs text-muted-foreground' role='note'>
                {disclaimer}
              </p>
            ) : null}
          </Card.Body>
        </div>
      </Card>

      <HealthProfileBreakdownModal
        show={breakdownOpen}
        onHide={closeBreakdown}
        axes={data?.axes}
        disclaimer={disclaimer}
      />
    </>
  );
}
